import React from 'react';
import {
  Popover,
  Box,
  Typography,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import MarkdownRenderer from 'react-markdown-renderer';
import { tableToName } from '../../config';

const parseContextContent = (rawContent) => {
  if (typeof rawContent !== 'string') {
    return { category: null, externalId: null, question: null, response: String(rawContent ?? '') };
  }
  const getMetadata = (txt) => {
    const metadata = txt.match(/\[[^\]]+\]\s*/g);
    return metadata ? metadata.map(m => m.replace(/\[|\]/g, '')) : [];
  }
  let content = rawContent.trim();
  const metadata = getMetadata(content);
  console.log(metadata);
  const unusedMetadata = [];
  let category = null;
  let externalId = null;
  let file = null;
  for(let m of metadata){
    if(m.startsWith('Category:')){
      category = m.split(':')[1];
      
    }else if(m.startsWith('ID:')){
      externalId = m.split(':')[1];
    }else if(m.startsWith('file:')){
      file = m.split(':')[1];
    }else{
      unusedMetadata.push(m);
    }
  }

  // Extract Question and Response segments if present
  let question = null;
  let response = content;
  const qIndex = content.toLowerCase().indexOf('question:');
  const rIndex = content.toLowerCase().indexOf('response:');

  if (qIndex !== -1 && rIndex !== -1 && rIndex > qIndex) {
    question = content.substring(qIndex + 'question:'.length, rIndex).trim();
    response = content.substring(rIndex + 'response:'.length).trim();
  }

  // Clean bracket metadata from response/question for display
  const stripMeta = (txt) => (txt || '').replace(/\[[^\]]+\]\s*/g, '').trim();

  return {
    category,
    externalId,
    question: stripMeta(question),
    response: stripMeta(unusedMetadata.map(m => `(${m})`).join(' ') + ' ' + response),
  };
};

const ContextItemCard = ({ item }) => {
  const { category, externalId, question, response, metadata } = parseContextContent(item.content);
  const similarityPercent = typeof item.similarity === 'number' ? Math.round(item.similarity * 1000) / 10 : null;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
        border: '1px solid rgba(102,126,234,0.25)',
        boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
      }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
        {item.table_name && <Chip size="small" label={tableToName[item.table_name]} color="primary" variant="outlined" />}
        {category && <Chip size="small" label={`Category: ${category}`} variant="outlined" />}
        {externalId && <Chip size="small" label={`Ocelot ID: ${externalId}`} variant="outlined" /> }
        <Chip size="small" label={`ID: ${item?.id}`} variant="outlined" />
        {similarityPercent !== null && (
          <Chip size="small" label={`Similarity: ${similarityPercent}%`} color="secondary" variant="outlined" />
        )}
      </Stack>
      {question && (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Q: {question}
        </Typography>
      )}
      {response && (
        <Box sx={{
          mt: 0.5,
          p: 1.25,
          borderRadius: 1.5,
          backgroundColor: 'rgba(255,255,255,0.6)',
          border: '1px solid rgba(102,126,234,0.15)'
        }}>
          <Typography component="div" variant="body2" sx={{ color: 'text.primary' }}>
            <MarkdownRenderer markdown={response} />
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const ContextPopover = ({ open, anchorEl, onClose, context,query }) => {
  const items = Array.isArray(context) ? context : [];

  return (
    <Popover
      open={Boolean(open && anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ zIndex: 15000 }}
      PaperProps={{
        sx: {
          width: { xs: 340, sm: 420 },
          maxWidth: '90vw',
          maxHeight: '70vh',
          overflow: 'hidden',
          borderRadius: 3,
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(102,126,234,0.25)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 8px 24px rgba(102,126,234,0.15)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(102,126,234,0.2)', flexShrink: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Query: "{query}"
        </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Query used to generate this response
          </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Context used ({items.length}) 
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Sources and snippets that informed this response
        </Typography>
      </Box>
      <Box sx={{ p: 2, overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
        {items.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No context available for this message.
          </Typography>
        )}

        {items.map((item, idx) => (
          <Box key={idx}>
            <ContextItemCard item={item} />
            {idx !== items.length - 1 && <Divider sx={{ my: 1.5 }} />}
          </Box>
        ))}
      </Box>
    </Popover>
  );
};

export default ContextPopover;


